import { Children, isValidElement, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx';
import { chipDotClass, type ChipTone } from '../Chip/Chip';

export type KanbanLaneProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  title: ReactNode;
  /** Stage colour, following the Chip tones (STAGE_TONE). */
  tone?: ChipTone;
  /** Formatted count of cards in the lane. */
  count?: ReactNode;
  /** Buttons at the end of the header (a visibility toggle, a collapse button). Pass IconButtons with variant ghost. */
  actions?: ReactNode;
  /** Highlights the lane while a card is dragged over it. Drag handlers go on the lane as props. */
  dropTarget?: boolean;
  /** Shown when the lane is empty. */
  empty?: ReactNode;
  /** Compact cards. Dragging must never be the only way to move one (offer a "Move to" Menu or Sheet). */
  children?: ReactNode;
};

/** A pipeline column: the stage with its dot and count, then a list of compact cards. */
export function KanbanLane({
  title,
  tone = 'neutral',
  count,
  actions,
  dropTarget = false,
  empty,
  children,
  className,
  ...rest
}: KanbanLaneProps) {
  const cards = Children.toArray(children);
  return (
    <section
      {...rest}
      className={cx(
        'flex min-w-64 flex-col gap-3 rounded-jc-container bg-jc-elevated p-3 transition-shadow duration-(--jc-duration-fast)',
        dropTarget && 'ring-2 ring-inset ring-jc-focus',
        className,
      )}
    >
      <div className="flex items-center gap-1">
        <h3 className="flex min-w-0 flex-1 items-center gap-2 px-1 pt-1 text-sm font-semibold text-jc-ink">
          <span aria-hidden="true" className={cx('size-2.5 shrink-0 rounded-full', chipDotClass(tone))} />
          <span className="min-w-0 flex-1 truncate">{title}</span>
          {count != null ? <span className="tabular-nums text-jc-ink-secondary">{count}</span> : null}
        </h3>
        {actions ? <div className="-my-2 flex shrink-0 items-center">{actions}</div> : null}
      </div>
      {cards.length ? (
        <ul className="flex flex-col gap-2">
          {cards.map((card, i) => (
            <li key={isValidElement(card) && card.key != null ? card.key : i}>{card}</li>
          ))}
        </ul>
      ) : empty ? (
        <p className="rounded-jc-info border border-dashed border-jc-border-control px-3 py-6 text-center text-sm text-jc-ink-secondary">
          {empty}
        </p>
      ) : null}
    </section>
  );
}
